import { Fragment } from 'react';

import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { StorefrontFirstMoveView } from '@zola/svc-marketplace-ts-types';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import P from '@zola/zola-ui/src/typography/Paragraphs';

import InquiryButton from '~/components/common/ui/inquiries/InquiryButton';
import useLastInquiryToStorefront from '~/components/common/ui/inquiries/useLastInquiryToStorefront';
import ShareButton from '~/components/common/ui/ShareButton/ShareButton';
import { useToast } from '~/hooks/useToast';
import { FirstMoveExpiration } from '~/pages/couples/inquiries/components/FirstMoveExpiration/FirstMoveExpiration';
import FavoriteCTA from '~/pages/couples/storefronts/components/favorites/FavoriteCTA';
import { CouplesStorefrontDetails } from '~/types/storefrontDetails';
import { useStickyScroll } from '~/util/stickyBtn';
import { isClaimed } from '~/util/storefrontUtils';
import { TrackingProductLocation } from '~/util/trackingHelper';

import { useStorefrontDetails } from '../contexts/StorefrontDetailsContext';
import UnclaimedModule from './UnclaimedModule';

import styles from './storefrontDetailsStickyFooter.module.less';

type FirstMoveInviteProps = {
  firstMove: StorefrontFirstMoveView;
};

const FirstMoveInvite = ({ firstMove }: FirstMoveInviteProps) => {
  if (firstMove) {
    return (
      <div>
        <P.BodyBase className={styles.firstMovePrompt}>
          This vendor wants to work with you!
        </P.BodyBase>
        <FirstMoveExpiration className={styles.firstMoveExpiration} firstMove={firstMove} />
      </div>
    );
  }
  return <Fragment />;
};

const RapidRepeatText = () => {
  return (
    <P.BodyBase className={styles.rapidRepeatText}>
      We saved your answers from last time—reach&nbsp;out&nbsp;in&nbsp;seconds!
    </P.BodyBase>
  );
};

type StorefrontDetailsStickyFooterProps = {
  forceShow?: boolean;
  showFavoriteButton?: boolean;
  showShareButton?: boolean;
  /** Optional click handler to fire on the inquire or first move buttons */
  onClick?: () => void;
};

export const StorefrontDetailsStickyFooter = ({
  forceShow = false,
  showFavoriteButton = true,
  showShareButton = true,
  onClick,
}: StorefrontDetailsStickyFooterProps) => {
  const { storefrontDetails, firstMove, isRapidRepeat, dismissFirstMove } =
    useStorefrontDetails<CouplesStorefrontDetails>();

  const stickFooterRef = useStickyScroll({
    showStickyButton: true,
    height: 70,
  });

  const {
    name,
    taxonomyKey,
    location: vendorLocation,
    uuid: storefrontUuid,
    slug: storefrontSlug,
  } = storefrontDetails;

  const imageUuid = storefrontDetails.coverGallery
    ? storefrontDetails.coverGallery[0]?.uuid
    : undefined;

  const lastInquiry = useLastInquiryToStorefront(storefrontUuid);
  const { positiveToast } = useToast();

  const renderStickyContainer = (children: JSX.Element) =>
    forceShow ? (
      children
    ) : (
      <div ref={stickFooterRef} className="hide">
        {children}
      </div>
    );

  return (
    <>
      {renderStickyContainer(
        <div className={styles.storefrontStickyInfo}>
          {isClaimed(storefrontDetails) ? (
            <Fragment>
              {(firstMove || isRapidRepeat) && !lastInquiry && (
                <div className={styles.actionNote}>
                  {firstMove ? <FirstMoveInvite firstMove={firstMove} /> : <RapidRepeatText />}
                </div>
              )}
              <div className={styles.actionButtons}>
                {!firstMove && (showFavoriteButton || showShareButton) && (
                  <div className={styles.shareFavorite}>
                    {showFavoriteButton && (
                      <FavoriteCTA
                        storefrontUuid={storefrontUuid}
                        storefrontCoverUuid={imageUuid}
                        buttonClassOverrides={styles.favoriteButton}
                      />
                    )}
                    {showShareButton && (
                      <ShareButton
                        variant="compact"
                        uuid={storefrontUuid}
                        trackingLocation={TrackingProductLocation.STOREFRONT_DETAIL_PAGE}
                        businessUnit={BUSINESS_UNIT.MARKETPLACE}
                        buttonClassName={styles.shareButton}
                        imageUuid={imageUuid}
                        details={storefrontDetails}
                        name={name}
                        slug={storefrontSlug}
                        taxonomyKey={taxonomyKey}
                        location={vendorLocation ?? undefined}
                        modalType="vendors"
                        modalHed=""
                        buttonV3Variant="secondary"
                      />
                    )}
                  </div>
                )}
                {firstMove && (
                  <ButtonV3
                    className={styles.ignoreButton}
                    variant="secondary"
                    onClick={() => {
                      onClick?.();
                      dismissFirstMove();
                      positiveToast({ headline: `Got it—we'll let them know` });
                    }}
                  >
                    Not interested
                  </ButtonV3>
                )}
                <InquiryButton
                  className={styles.inquiryButton}
                  section="STICKY"
                  position={99}
                  data-testid="inquiry-button"
                  firstMoveUuid={firstMove?.uuid}
                  onClick={onClick}
                />
              </div>
            </Fragment>
          ) : (
            <UnclaimedModule
              className={styles.unclaimedModule}
              showFavoriteButton={false}
              onClick={onClick}
            />
          )}
        </div>
      )}
    </>
  );
};
